import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import TextController from "../../components/form-fields/TextController";
import SelectController from "../../components/form-fields/SelectController";

const engagementSchema = z.object({
  client: z.number(),
  type: z.enum(["call", "meeting", "note", "email"], {
    required_error: "Engagement type is required.",
  }),
  description: z
    .string()
    .trim()
    .min(1, "Description is required.")
    .min(3, "Description must be at least 3 characters."),
  start_date: z
    .string()
    .min(1, "Start date is required.")
    .refine((value) => !isNaN(Date.parse(value)), "Invalid date format."),
  end_date: z
    .string()
    .min(1, "End date is required.")
    .refine((value) => !isNaN(Date.parse(value)), "Invalid date format."),
});

type EngagementValues = z.infer<typeof engagementSchema>;

interface EngagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EngagementValues) => void;
}

const EngagementModal: React.FC<EngagementModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const { id } = useParams();
  const form = useForm<EngagementValues>({
    resolver: zodResolver(engagementSchema),
    defaultValues: {
      client: Number(id),
      type: "call",
      description: "",
      start_date: "",
      end_date: "",
    },
  });

  const onSubmit = (data: EngagementValues) => {
    onSave(data);
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Engagement</DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="mb-4">
              <SelectController
                name="type"
                label="Engagement Type"
                options={[
                  { value: "call", label: "Call" },
                  { value: "meeting", label: "Meeting" },
                  { value: "note", label: "Note" },
                  { value: "email", label: "Email" },
                ]}
                defaultValue="call"
              />
            </div>
            <div className="mb-4">
              <TextController
                name="description"
                label="Description"
                placeholder="Enter description"
              />
            </div>
            <div className="mb-4">
              <TextController
                name="start_date"
                label="Start Date"
                type="datetime-local"
                placeholder="Select start date and time"
              />
            </div>
            <div className="mb-4">
              <TextController
                name="end_date"
                label="End Date"
                type="datetime-local"
                placeholder="Select end date and time"
              />
            </div>
            <DialogFooter>
              <Button type="submit">Add Engagement</Button>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default EngagementModal;
